import { NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  HouseSimple,
  Scales,
  SignOut,
  Files,
  ChartLine,
  Bell,
  GearSix,
  ShieldCheck,
  Lifebuoy,
  Microphone,
  Sparkle,
  CaretDown,
  List,
  ChatTeardropText,
} from "@phosphor-icons/react";
import Logo from "../common/Logo";
import { useNotifications } from "../../contexts/NotificationsContext";
import { useAuth } from "../../contexts/AuthContext";
import { hasModuleAccess } from "../../utils/permissions";
import { isAdminRole } from "../../utils/roles";

const NAV_ITEMS = [
  { path: "/chat",              label: "Chat",               icon: Sparkle,                 testId: "nav-chat" },
  { path: "/",                  label: "Projects",           icon: HouseSimple, exact: true, testId: "nav-projects", permission: "projects",
    children: [
      { path: "/financials",        label: "Financials",         icon: ChartLine,   testId: "nav-financials", permission: "financials" },
      { path: "/recordings",        label: "Record",             icon: Microphone,  testId: "nav-recordings", permission: "recordings" },
    ],
  },
  { path: "/files",             label: "Explore Files",      icon: Files,                   testId: "nav-files", permission: "onedrive" },
  { path: "/drawing-evaluation",label: "Drawing Evaluation", icon: Scales,                  testId: "nav-drawing-evaluation", permission: "drawing_evaluation" },
];

export default function Sidebar({ onLogout, widgetVisible, onToggleWidget }) {
  const location          = useLocation();
  const { user }          = useAuth();
  const initiallyMobile = typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches;
  const [isMobile, setIsMobile] = useState(initiallyMobile);
  const [collapsed, setCollapsed] = useState(initiallyMobile);
  const [expandedItems, setExpandedItems] = useState({ "/": true }); // Projects expanded by default
  const { unreadCount, trayOpen, setTrayOpen }  = useNotifications();

  useEffect(() => {
    const mobileQuery = window.matchMedia("(max-width: 767px)");
    const handleViewportChange = (event) => {
      setIsMobile(event.matches);
      setCollapsed(event.matches);
    };

    mobileQuery.addEventListener("change", handleViewportChange);
    return () => mobileQuery.removeEventListener("change", handleViewportChange);
  }, []);

  useEffect(() => {
    if (isMobile) setCollapsed(true);
  }, [isMobile, location.pathname]);

  const isActive = (item) =>
    item.exact
      ? location.pathname === item.path || (item.path === "/" && location.pathname.startsWith("/projects"))
      : location.pathname.startsWith(item.path);

  const isChildActive = (item) =>
    item.children?.some((child) => location.pathname.startsWith(child.path));

  // Filter navigation items based on user permissions
  const visibleNavItems = NAV_ITEMS.filter((item) => !item.permission || hasModuleAccess(user, item.permission));

  const toggleExpand = (path) => {
    setExpandedItems((prev) => ({ ...prev, [path]: !prev[path] }));
  };

  const sidebarWidth = isMobile
    ? (collapsed ? "w-0 border-r-0 overflow-visible" : "fixed inset-y-0 left-0 z-50 w-64 shadow-xl")
    : (collapsed ? "w-16" : "w-64");

  return (
    <>
    {isMobile && !collapsed && (
      <button
        type="button"
        aria-label="Close navigation menu"
        onClick={() => setCollapsed(true)}
        className="fixed inset-0 z-40 bg-black/20"
        data-testid="mobile-sidebar-backdrop"
      />
    )}
    <aside
      className={`flex-shrink-0 bg-white border-r border-slate-200 flex flex-col h-screen transition-[width,transform] duration-200 ${sidebarWidth}`}
      data-testid="sidebar"
    >
      {/* Brand and collapse control */}
      <div className={`border-b border-slate-200 flex items-center flex-shrink-0 ${
        isMobile && collapsed
          ? "fixed left-2 top-2 z-50 border-0 p-0"
          : collapsed
            ? "px-2 py-4 justify-center"
            : "px-4 py-4 gap-2.5"
      }`}>
        {!collapsed && <Logo size={28} className="flex-shrink-0" />}
        {!collapsed && (
          <div className="min-w-0">
            <p className="font-cabinet text-xs font-black tracking-tight text-slate-900 leading-none">
              ENGINEERING HUB
            </p>
            <p className="text-[10px] text-slate-400 font-medium tracking-wide mt-0.5 leading-none">
              PROJECT PLATFORM
            </p>
          </div>
        )}
        <button
          type="button"
          onClick={() => setCollapsed(!collapsed)}
          data-testid="sidebar-collapse-btn"
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          aria-expanded={!collapsed}
          className={`${collapsed ? "" : "ml-auto"} p-2 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors`}
        >
          <List size={20} weight="bold" />
        </button>
      </div>

      {/* Navigation */}
      {(!isMobile || !collapsed) && <nav className="flex-1 px-2 py-4 overflow-y-auto">
        {!collapsed && (
          <p className="text-[10px] font-semibold tracking-[0.15em] text-slate-400 uppercase px-2 mb-2">
            Navigation
          </p>
        )}
        <ul className="space-y-0.5">
          {visibleNavItems.map((item) => {
            const Icon   = item.icon;
            const active = isActive(item) || isChildActive(item);
            const expanded = expandedItems[item.path] !== false; // default open
            const hasChildren = item.children && item.children.length > 0;
            const visibleChildren = hasChildren
              ? item.children.filter((child) => !child.permission || hasModuleAccess(user, child.permission))
              : [];

            return (
              <li key={item.path}>
                <div className="flex items-center">
                  <NavLink
                    to={item.path}
                    data-testid={item.testId}
                    title={collapsed ? item.label : undefined}
                    className={`
                      flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-md flex-1
                      transition-colors select-none group relative
                      ${collapsed ? "justify-center" : ""}
                      ${active
                        ? "bg-primary/8 text-primary"
                        : "text-slate-500 hover:text-slate-900 hover:bg-slate-50"
                      }
                    `}
                  >
                    <Icon
                      size={collapsed ? 20 : 16}
                      weight={active ? "bold" : "regular"}
                      className={active ? "text-primary" : "text-slate-400 group-hover:text-slate-600"}
                    />
                    {!collapsed && <span className="flex-1">{item.label}</span>}
                  </NavLink>
                  {item.path === "/chat" && !collapsed && (
                    <button
                      type="button"
                      onClick={onToggleWidget}
                      title={widgetVisible ? "Hide AI chat bubble" : "Show AI chat bubble"}
                      aria-label={widgetVisible ? "Hide AI chat bubble" : "Show AI chat bubble"}
                      data-testid="toggle-widget-btn"
                      className={`p-1.5 rounded transition-colors ml-0.5 ${
                        widgetVisible
                          ? "text-primary hover:text-primary/70 hover:bg-primary/5"
                          : "text-slate-300 hover:text-slate-500 hover:bg-slate-100"
                      }`}
                    >
                      <ChatTeardropText size={14} weight={widgetVisible ? "fill" : "regular"} />
                    </button>
                  )}
                  {!collapsed && hasChildren && visibleChildren.length > 0 && (
                    <button
                      onClick={() => toggleExpand(item.path)}
                      className="p-1.5 text-slate-400 hover:text-slate-600 rounded transition-colors"
                      data-testid={`${item.testId}-expand`}
                    >
                      <CaretDown
                        size={12}
                        className={`transition-transform duration-200 ${expanded ? "" : "-rotate-90"}`}
                      />
                    </button>
                  )}
                </div>

                {/* Children / sub-items */}
                {!collapsed && hasChildren && expanded && visibleChildren.length > 0 && (
                  <ul className="ml-5 mt-0.5 space-y-0.5 border-l border-slate-100 pl-3">
                    {visibleChildren.map((child) => {
                      const ChildIcon = child.icon;
                      const childActive = location.pathname.startsWith(child.path);
                      return (
                        <li key={child.path} className="flex items-center">
                          <NavLink
                            to={child.path}
                            data-testid={child.testId}
                            className={`
                              flex items-center gap-2.5 px-2.5 py-2 text-[13px] font-medium rounded-md flex-1
                              transition-colors select-none group
                              ${childActive
                                ? "bg-primary/8 text-primary"
                                : "text-slate-500 hover:text-slate-900 hover:bg-slate-50"
                              }
                            `}
                          >
                            <ChildIcon
                              size={14}
                              weight={childActive ? "bold" : "regular"}
                              className={childActive ? "text-primary" : "text-slate-400 group-hover:text-slate-600"}
                            />
                            <span>{child.label}</span>
                          </NavLink>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      </nav>}

      {/* Footer */}
      {(!isMobile || !collapsed) && <div className={`pb-4 border-t border-slate-200 pt-3 space-y-0.5 flex-shrink-0 ${collapsed ? "px-2" : "px-3"}`}>
        {/* Notifications button */}
        <button
          onClick={() => setTrayOpen(!trayOpen)}
          data-testid="nav-notifications-btn"
          title={collapsed ? "Notifications" : undefined}
          className={`
            w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-md
            transition-colors select-none group
            ${collapsed ? "justify-center" : ""}
            ${trayOpen
              ? "bg-violet-50 text-violet-700"
              : "text-slate-500 hover:text-slate-900 hover:bg-slate-50"
            }
          `}
        >
          <Bell
            size={16}
            weight={trayOpen ? "fill" : "regular"}
            className={trayOpen ? "text-violet-600" : "text-slate-400 group-hover:text-slate-600"}
          />
          {!collapsed && <span className="flex-1 text-left">Notifications</span>}
          {unreadCount > 0 && (
            <span
              className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold rounded-full bg-violet-600 text-white ${
                collapsed ? "absolute -top-1 -right-1" : ""
              }`}
              data-testid="notification-unread-badge"
            >
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </button>

        {/* Admin Portal shortcut - only for org_admin / super_admin / admin */}
        {isAdminRole(user?.role) && (
          <NavLink
            to="/admin"
            data-testid="nav-admin-portal"
            title={collapsed ? "Admin Portal" : undefined}
            className={({ isActive }) => `w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-md
              transition-colors select-none group
              ${collapsed ? "justify-center" : ""}
              ${isActive
                ? "bg-primary/8 text-primary"
                : "text-slate-500 hover:text-slate-900 hover:bg-slate-50"
              }`}
          >
            {({ isActive }) => (
              <>
                <ShieldCheck
                  size={16}
                  weight={isActive ? "bold" : "regular"}
                  className={isActive ? "text-primary" : "text-slate-400 group-hover:text-slate-600"}
                />
                {!collapsed && <span className="flex-1">Admin Portal</span>}
              </>
            )}
          </NavLink>
        )}

        {/* Help & Guide */}
        <NavLink
          to="/help"
          data-testid="nav-help"
          title={collapsed ? "Help & Guide" : undefined}
          className={({ isActive }) => `
            w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-md
            transition-colors select-none group
            ${collapsed ? "justify-center" : ""}
            ${isActive
              ? "bg-primary/8 text-primary"
              : "text-slate-500 hover:text-slate-900 hover:bg-slate-50"
            }
          `}
        >
          {({ isActive }) => (
            <>
              <Lifebuoy
                size={16}
                weight={isActive ? "bold" : "regular"}
                className={isActive ? "text-primary" : "text-slate-400 group-hover:text-slate-600"}
              />
              {!collapsed && <span className="flex-1">Help &amp; Guide</span>}
            </>
          )}
        </NavLink>

        {/* Settings */}
        {hasModuleAccess(user, "settings") && (
          <NavLink
            to="/settings"
            data-testid="nav-settings"
            title={collapsed ? "Settings" : undefined}
            className={({ isActive }) => `
              w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-md
              transition-colors select-none group
              ${collapsed ? "justify-center" : ""}
              ${isActive
                ? "bg-primary/8 text-primary"
                : "text-slate-500 hover:text-slate-900 hover:bg-slate-50"
              }
            `}
          >
            {({ isActive }) => (
              <>
                <GearSix
                  size={16}
                  weight={isActive ? "bold" : "regular"}
                  className={isActive ? "text-primary" : "text-slate-400 group-hover:text-slate-600"}
                />
                {!collapsed && <span className="flex-1">Settings</span>}
              </>
            )}
          </NavLink>
        )}

        {/* Log out */}
        <button
          onClick={onLogout}
          data-testid="sidebar-logout-button"
          title={collapsed ? "Log Out" : undefined}
          className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors ${
            collapsed ? "justify-center" : ""
          }`}
        >
          <SignOut size={16} className="text-slate-400" />
          {!collapsed && "Log Out"}
        </button>
      </div>}
    </aside>
    </>
  );
}
